import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeId = 'dark' | 'midnight' | 'slate';

export interface Theme {
  id: ThemeId;
  name: string;
  description: string;
}

export const themes: Record<ThemeId, Theme> = {
  dark: {
    id: 'dark',
    name: 'Dark',
    description: 'Default dark theme',
  },
  midnight: {
    id: 'midnight',
    name: 'Midnight',
    description: 'Deep blue-black theme',
  },
  slate: {
    id: 'slate',
    name: 'Slate',
    description: 'Neutral gray theme',
  },
};

interface ThemeState {
  currentTheme: ThemeId;
  setTheme: (theme: ThemeId) => void;
  getTheme: () => Theme;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      currentTheme: 'dark',

      setTheme: (themeId: ThemeId) => {
        set({ currentTheme: themeId });
        applyTheme(themeId);
      },

      getTheme: () => themes[get().currentTheme],
    }),
    {
      name: 'overwatch-theme',
      onRehydrateStorage: () => (state) => {
        if (state) {
          applyTheme(state.currentTheme);
        }
      },
    }
  )
);

function applyTheme(themeId: ThemeId) {
  document.documentElement.setAttribute('data-theme', themeId);
  // All theming handled via CSS — the data-theme attribute
  // can be used for theme-specific overrides if needed.
  // We keep the class 'dark' on html for shadcn/tailwind dark mode.
  document.documentElement.classList.add('dark');
}

if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('overwatch-theme');
  if (stored) {
    try {
      const { state } = JSON.parse(stored);
      if (state?.currentTheme) {
        applyTheme(state.currentTheme as ThemeId);
      }
    } catch {
      applyTheme('dark');
    }
  } else {
    applyTheme('dark');
  }
}
